import { useSidebar } from "@/hooks/useSidebar";
import { Link, Path } from "@/router";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent
} from "react";
import { createPortal } from "react-dom";
import ClosedNavigationItem from "./ClosedNavigationItem";
import OpenNavigationItem from "./OpenNavigationItem";
import type { NavigationItemProps } from "./types";
import { useSidebarContext } from "./useSidebarContext";
import type { NavFlyoutLink } from "../types";
import { computeFlyoutPosition, FLYOUT_MIN_WIDTH_PX, type FlyoutPosition } from "./flyoutPosition";

const HOVER_CLOSE_DELAY_MS = 100;

type Props<P extends Path> = NavigationItemProps<P> & {
  flyoutLinks: NavFlyoutLink<P>[];
};

export default function NavigationLinkWithFlyout<P extends Path>({ flyoutLinks, ...props }: Props<P>) {
  const { isSidebarOpen } = useSidebar();
  const { forceExpanded, setFlyoutPointerInside } = useSidebarContext();
  const isExpanded = forceExpanded || isSidebarOpen;
  const [open, setOpen] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState<FlyoutPosition | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const pendingFlyoutFocusRef = useRef(false);
  const flyoutId = useId();
  const triggerId = useId();
  const Component = isExpanded ? OpenNavigationItem : ClosedNavigationItem;

  const setPointerInsideFlyout = useCallback(
    (inside: boolean) => {
      setFlyoutPointerInside?.(inside);
    },
    [setFlyoutPointerInside]
  );

  const updateFlyoutPosition = useCallback(() => {
    if (!anchorRef.current) return;
    setFlyoutPosition(computeFlyoutPosition(anchorRef.current));
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
      setFlyoutPosition(null);
      setPointerInsideFlyout(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimeout, setPointerInsideFlyout]);

  const closeFlyout = useCallback(() => {
    clearCloseTimeout();
    setOpen(false);
    setFlyoutPosition(null);
    pendingFlyoutFocusRef.current = false;
    setPointerInsideFlyout(false);
  }, [clearCloseTimeout, setPointerInsideFlyout]);

  const handleOpen = useCallback(() => {
    if (flyoutLinks.length === 0) return;
    clearCloseTimeout();
    setOpen(true);
    updateFlyoutPosition();
  }, [clearCloseTimeout, flyoutLinks.length, updateFlyoutPosition]);

  const isFocusWithinFlyout = useCallback(
    (target: EventTarget | null) => {
      if (!target || !(target instanceof Node)) return false;
      const flyout = document.getElementById(flyoutId);
      return anchorRef.current?.contains(target) || flyout?.contains(target);
    },
    [flyoutId]
  );

  const handleTriggerBlur = useCallback(
    (event: ReactFocusEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (isFocusWithinFlyout(event.relatedTarget)) return;
      closeFlyout();
    },
    [closeFlyout, isFocusWithinFlyout]
  );

  const handleTriggerKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (event.key === "Escape" && open) {
        event.preventDefault();
        event.stopPropagation();
        closeFlyout();
        return;
      }

      if (event.key === "ArrowDown" && flyoutLinks.length > 0) {
        event.preventDefault();
        pendingFlyoutFocusRef.current = true;
        handleOpen();
      }
    },
    [closeFlyout, flyoutLinks.length, handleOpen, open]
  );

  const focusFlyoutLink = useCallback(
    (index: number) => {
      const item = document.getElementById(`${flyoutId}-item-${index}`);
      item?.focus();
    },
    [flyoutId]
  );

  const handleFlyoutKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement | HTMLAnchorElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeFlyout();
        document.getElementById(triggerId)?.focus();
        return;
      }

      const currentIndex = flyoutLinks.findIndex((_, index) => {
        const item = document.getElementById(`${flyoutId}-item-${index}`);
        return item === document.activeElement;
      });

      if (event.key === "ArrowDown" && currentIndex < flyoutLinks.length - 1) {
        event.preventDefault();
        focusFlyoutLink(currentIndex + 1);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (currentIndex <= 0) {
          closeFlyout();
          document.getElementById(triggerId)?.focus();
          return;
        }
        focusFlyoutLink(currentIndex - 1);
      }
    },
    [closeFlyout, flyoutLinks, focusFlyoutLink, flyoutId, triggerId]
  );

  const handleFlyoutPointerEnter = useCallback(() => {
    clearCloseTimeout();
    setPointerInsideFlyout(true);
    handleOpen();
  }, [clearCloseTimeout, handleOpen, setPointerInsideFlyout]);

  const handleFlyoutPointerLeave = useCallback(() => {
    setPointerInsideFlyout(false);
    scheduleClose();
  }, [scheduleClose, setPointerInsideFlyout]);

  useLayoutEffect(() => {
    if (!open) return;

    updateFlyoutPosition();

    const handleReposition = () => updateFlyoutPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updateFlyoutPosition]);

  useLayoutEffect(() => {
    if (!open || !pendingFlyoutFocusRef.current) return;
    pendingFlyoutFocusRef.current = false;
    focusFlyoutLink(0);
  }, [open, focusFlyoutLink]);

  useLayoutEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  if (!isExpanded) {
    return <Component {...props} />;
  }

  const flyoutPanel =
    open && flyoutLinks.length > 0 && flyoutPosition && typeof document !== "undefined"
      ? createPortal(
          <nav
            id={flyoutId}
            data-nav-flyout-panel
            data-open={open}
            className="fixed z-[200] min-w-[240px] overflow-y-auto rounded-lg bg-extended-pgadarknavy-brand-color shadow-lg border border-white/10 py-1"
            style={{
              left: flyoutPosition.left,
              top: flyoutPosition.top,
              height: flyoutPosition.height,
              minWidth: FLYOUT_MIN_WIDTH_PX
            }}
            aria-labelledby={triggerId}
            onMouseEnter={handleFlyoutPointerEnter}
            onMouseLeave={handleFlyoutPointerLeave}
            onBlur={(event) => {
              if (isFocusWithinFlyout(event.relatedTarget)) return;
              closeFlyout();
            }}
            onKeyDown={handleFlyoutKeyDown}
          >
            {flyoutLinks.map((item, index) => (
              <Link
                key={`${item.label}-${index}`}
                id={`${flyoutId}-item-${index}`}
                {...item.link}
                className="block px-4 py-3 text-label-large text-extended-pgadarknavy-brand-on-color hover:bg-extended-pgadarknavy-brand-color-bright"
                onKeyDown={handleFlyoutKeyDown}
              >
                {item.label}
              </Link>
            ))}
          </nav>,
          document.body
        )
      : null;

  return (
    <>
      <div ref={anchorRef} className="relative w-full" onMouseEnter={handleOpen} onMouseLeave={scheduleClose}>
        <OpenNavigationItem
          {...props}
          menuTriggerProps={{
            id: triggerId,
            "aria-haspopup": true,
            "aria-expanded": open,
            "aria-controls": open ? flyoutId : undefined,
            onBlur: handleTriggerBlur,
            onKeyDown: handleTriggerKeyDown
          }}
        />
      </div>
      {flyoutPanel}
    </>
  );
}
